import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AdminHeader } from "./AdminLayout";
import API_ROUTES from "../../hooks/apiRoutes";
import {
  apiGet,
  apiSend,
  apiUploadImagem,
  useAdminState,
  Erro,
  Toast,
} from "../../hooks/useAdminApi";

/**
 * BANNERS — o carrossel do topo da home.
 *
 * É a primeira coisa que a criança vê. Por isso a tela inteira é construída em volta da
 * PRÉVIA: o card É o banner, na proporção real, com o texto sobreposto. Uma tabela com a
 * URL da imagem obrigaria o admin a abrir cada link numa aba pra descobrir o que subiu no
 * ar — que é exatamente o erro que esta tela existe pra evitar.
 *
 * A API fala `banner_*` (banner_url, banner_title…) na entrada E na saída. Mantemos esse
 * vocabulário de ponta a ponta em vez de traduzir pra camelCase: cada mapeamento a mais é
 * uma chance a mais de mandar o campo com o nome errado e o backend descartá-lo calado.
 */

/**
 * A proporção do carrossel REAL do app (ImageCarousel.css: `aspect-ratio: 16 / 9`).
 *
 * ⚠️ O mock do design usava 1107/500 (~2,2:1). Se a prévia seguisse o mock, ela MENTIRIA:
 * o `object-fit: cover` recorta de forma diferente em cada proporção, então o admin
 * aprovaria um banner cujo personagem aparece cortado no app. A prévia só vale se for
 * fiel ao que a criança vê — e a verdade é o CSS que está no ar, não o mock.
 *
 * Se um dia o carrossel mudar de proporção, este valor muda junto (é o único lugar).
 */
const PROPORCAO = "16 / 9";

const VAZIO = {
  banner_title: "",
  banner_subtitle: "",
  banner_action_url: "",
  banner_url: "",
};

export default function Banners() {
  const { tema, setTema } = useOutletContext();
  const { erro, toast, rodar, limparErro } = useAdminState();

  const [banners, setBanners] = useState(null);
  const [form, setForm] = useState(null); // banner em edição — VAZIO quando é novo
  const [excluindo, setExcluindo] = useState(null);

  const carregar = () =>
    rodar(async () => {
      // A lista vem embrulhada: { banners: [...] }, não um array direto.
      const r = await apiGet(API_ROUTES.BANNERS.default());
      setBanners(r.banners || []);
    });

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const salvar = (d) =>
    rodar(async () => {
      // Mandamos os campos vazios também: é assim que o admin APAGA um título que não
      // quer mais. Omitir a chave faz o backend simplesmente preservar o valor antigo.
      const corpo = {
        banner_url: d.banner_url.trim(),
        banner_title: d.banner_title.trim(),
        banner_subtitle: d.banner_subtitle.trim(),
        banner_action_url: d.banner_action_url.trim(),
      };

      if (d.banner_id) await apiSend(API_ROUTES.BANNERS.update(d.banner_id), "PUT", corpo);
      else await apiSend(API_ROUTES.BANNERS.default(), "POST", corpo);

      setForm(null);
      await carregar();
    }, d.banner_id ? "Banner atualizado." : "Banner criado.");

  const excluir = (b) =>
    rodar(async () => {
      await apiSend(API_ROUTES.BANNERS.delete(b.banner_id), "DELETE");
      setExcluindo(null);
      await carregar();
    }, "Banner excluído.");

  /**
   * Reordena o carrossel. Manda a lista COMPLETA na ordem nova — o backend aplica numa
   * transação. Mandar um a um deixaria o carrossel embaralhado no meio do caminho, e ele
   * é a primeira coisa que a criança vê.
   */
  const mover = (i, d) => {
    const nova = [...(banners || [])];
    const j = i + d;
    if (j < 0 || j >= nova.length) return;
    [nova[i], nova[j]] = [nova[j], nova[i]];

    rodar(async () => {
      await apiSend(API_ROUTES.BANNERS.sort(), "PUT", {
        banners: nova.map((b, k) => ({ banner_id: b.banner_id, banner_index: k + 1 })),
      });
      await carregar();
    }, "Ordem do carrossel atualizada.");
  };

  const total = banners?.length || 0;

  return (
    <>
      <AdminHeader
        tema={tema}
        setTema={setTema}
        titulo="Banners"
        subtitulo="O carrossel do topo da home — a primeira coisa que a criança vê."
      />

      <div className="adm__body">
        <Erro erro={erro} onFechar={limparErro} />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 18,
          }}
        >
          <span style={{ fontSize: 13, color: "var(--text2)" }}>
            {total} {total === 1 ? "banner" : "banners"} · prévia na proporção real
          </span>
          <button className="adm__btn" onClick={() => setForm({ ...VAZIO })}>
            + Novo banner
          </button>
        </div>

        {!banners && <p className="adm__vazio">Carregando…</p>}

        {banners && total === 0 && (
          <div className="adm__card">
            <div className="adm__vazio">
              <strong style={{ color: "var(--text2)", display: "block", marginBottom: 5 }}>
                Nenhum banner no ar
              </strong>
              O topo da home fica sem carrossel até você cadastrar o primeiro.
            </div>
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
            gap: 18,
          }}
        >
          {(banners || []).map((b, i) => (
            <div key={b.banner_id}>
              {/* Botão, e não div clicável: o card é a área de edição e precisa
                  responder ao teclado como qualquer outro controle. */}
              <button
                onClick={() => setForm({ ...VAZIO, ...b })}
                title="Editar banner"
                style={{
                  display: "block",
                  width: "100%",
                  padding: 0,
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  font: "inherit",
                  textAlign: "left",
                  position: "relative",
                }}
              >
                <Previa
                  url={b.banner_url}
                  titulo={b.banner_title}
                  subtitulo={b.banner_subtitle}
                />
                {/* A posição no carrossel. Sem isto o admin não sabe qual vem primeiro —
                    e "primeiro" é o único que a maioria das crianças vai ver. */}
                <span
                  className="adm__chip"
                  style={{
                    position: "absolute",
                    top: 9,
                    left: 9,
                    background: "rgba(0,0,0,.72)",
                    color: "#fff",
                    border: "none",
                    fontWeight: 700,
                  }}
                >
                  {i + 1}º
                </span>
              </button>

              {/* Reordenar. O backend recebe o carrossel INTEIRO numa transação — mandar
                  um a um deixaria a home embaralhada no meio do caminho. */}
              <div style={{ display: "flex", gap: 5, marginTop: 8 }}>
                <button
                  className="adm__btnGhost"
                  disabled={i === 0}
                  onClick={() => mover(i, -1)}
                  style={{ padding: "4px 9px", fontSize: 11 }}
                  title="Adiantar no carrossel"
                >
                  ◀
                </button>
                <button
                  className="adm__btnGhost"
                  disabled={i === (banners || []).length - 1}
                  onClick={() => mover(i, 1)}
                  style={{ padding: "4px 9px", fontSize: 11 }}
                  title="Atrasar no carrossel"
                >
                  ▶
                </button>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                  marginTop: 9,
                  padding: "0 2px",
                }}
              >
                {b.banner_action_url ? (
                  <span
                    className="adm__code"
                    title={b.banner_action_url}
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                  >
                    {b.banner_action_url}
                  </span>
                ) : (
                  <span style={{ fontSize: 11.5, color: "var(--text3)" }}>
                    Sem link — o banner não leva a lugar nenhum
                  </span>
                )}

                <button
                  className="adm__btnGhost"
                  onClick={() => setExcluindo(b)}
                  style={{
                    color: "var(--cancel)",
                    borderColor: "rgba(255,90,95,.35)",
                    flex: "0 0 auto",
                    padding: "5px 11px",
                  }}
                >
                  Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {form && (
        <ModalBanner form={form} onFechar={() => setForm(null)} onSalvar={salvar} />
      )}

      {excluindo && (
        <ConfirmarExclusao
          banner={excluindo}
          onCancelar={() => setExcluindo(null)}
          onOk={() => excluir(excluindo)}
        />
      )}

      <Toast toast={toast} />
    </>
  );
}

/**
 * O banner como o app o desenha: imagem cobrindo o quadro, texto por cima.
 *
 * Uma URL quebrada NÃO pode derrubar o layout — o quadro continua de pé e diz o que
 * aconteceu, porque "a imagem não carregou aqui" é o mesmo que a criança veria na home.
 */
function Previa({ url, titulo, subtitulo, tracejado }) {
  const [falhou, setFalhou] = useState(false);

  // URL nova merece uma nova tentativa: sem isto, o erro de uma imagem anterior
  // grudaria na prévia enquanto o admin digita a URL certa.
  useEffect(() => setFalhou(false), [url]);

  const temUrl = Boolean(url && url.trim());
  const mostraImagem = temUrl && !falhou;

  return (
    <div
      style={{
        position: "relative",
        aspectRatio: PROPORCAO,
        borderRadius: 14,
        overflow: "hidden",
        background: "var(--elev)",
        border: tracejado ? "1.5px dashed var(--border)" : "1px solid var(--border)",
      }}
    >
      {mostraImagem && (
        <img
          src={url}
          alt=""
          onError={() => setFalhou(true)}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      )}

      {!mostraImagem && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            textAlign: "center",
            padding: 18,
            color: "var(--text3)",
            fontSize: 12.5,
            lineHeight: 1.6,
          }}
        >
          {temUrl ? (
            <span style={{ color: "var(--orange)" }}>
              A imagem não carregou.
              <br />
              No topo da home, a criança veria este espaço vazio.
            </span>
          ) : (
            <span>Cole a URL da imagem para ver a prévia</span>
          )}
        </div>
      )}

      {/* O gradiente não é enfeite: sem ele o texto some numa imagem clara.
          É o MESMO do carrossel real (ImageCarousel.css) — de baixo pra cima, não da
          esquerda pra direita. Se a prévia usasse outro, o admin veria o texto legível
          aqui e ilegível no app. */}
      {mostraImagem && (titulo || subtitulo) && (
        <>
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(transparent, rgba(0, 0, 0, 0.8))",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: 0,
              bottom: 0,
              padding: "22px 24px",
              maxWidth: "75%",
            }}
          >
            {titulo && (
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 900,
                  letterSpacing: -0.4,
                  color: "#fff",
                  lineHeight: 1.15,
                  marginBottom: 4,
                }}
              >
                {titulo}
              </div>
            )}
            {subtitulo && (
              <div
                style={{
                  fontSize: 13,
                  color: "rgba(255,255,255,.85)",
                  lineHeight: 1.35,
                }}
              >
                {subtitulo}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ModalBanner({ form, onFechar, onSalvar }) {
  const [d, setD] = useState(form);
  const [enviando, setEnviando] = useState(false);
  const [erroUpload, setErroUpload] = useState(null);

  const campo = (k, v) => setD((a) => ({ ...a, [k]: v }));

  // A imagem é o banner: sem ela não há o que mostrar, e o backend recusa o cadastro.
  const valido = Boolean(d.banner_url.trim());

  return (
    <div className="adm__modalFundo" onClick={onFechar}>
      <div
        className="adm__modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(560px, 100%)" }}
      >
        <h3>{d.banner_id ? "Editar banner" : "Novo banner"}</h3>
        <p className="adm__modalHint">
          O texto vai <strong>sobreposto</strong> à imagem. É assim que ele aparece no topo
          da home — confira na prévia antes de salvar.
        </p>

        <div className="adm__campo">
          <label>Imagem — prévia no formato real</label>
          <Previa
            url={d.banner_url}
            titulo={d.banner_title}
            subtitulo={d.banner_subtitle}
            tracejado
          />
        </div>

        <div className="adm__campo">
          <label>Imagem</label>

          {/* O upload vai pro Bunny (o mesmo CDN das thumbnails) e devolve a URL pronta.
              Antes o admin tinha que hospedar a imagem por fora e colar o link — o campo
              de URL continua aí para quem já tem a imagem em algum lugar. */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <label
              className="adm__btnGhost"
              style={{ cursor: enviando ? "wait" : "pointer", opacity: enviando ? 0.6 : 1 }}
            >
              {enviando ? "Enviando…" : "⬆ Enviar imagem"}
              <input
                type="file"
                accept="image/*"
                hidden
                disabled={enviando}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setErroUpload(null);
                  setEnviando(true);
                  try {
                    campo("banner_url", await apiUploadImagem(file));
                  } catch (err) {
                    setErroUpload(err.message);
                  } finally {
                    setEnviando(false);
                    e.target.value = ""; // permite reenviar o MESMO arquivo depois
                  }
                }}
              />
            </label>
            <span style={{ fontSize: 12, color: "var(--text3)" }}>ou cole a URL abaixo</span>
          </div>

          <input
            className="adm__input"
            value={d.banner_url}
            onChange={(e) => campo("banner_url", e.target.value)}
            placeholder="https://…/banner.jpg"
            style={{ fontFamily: '"Roboto Mono", monospace', fontSize: 12.5 }}
          />

          {erroUpload && (
            <p style={{ fontSize: 12, color: "var(--cancel)", margin: "7px 0 0" }}>
              {erroUpload}
            </p>
          )}
        </div>

        <div className="adm__campo">
          <label>Título (opcional)</label>
          <input
            className="adm__input"
            value={d.banner_title}
            onChange={(e) => campo("banner_title", e.target.value)}
            placeholder="ex.: Novo: Moisés, o Libertador"
          />
        </div>

        <div className="adm__campo">
          <label>Subtítulo (opcional)</label>
          <input
            className="adm__input"
            value={d.banner_subtitle}
            onChange={(e) => campo("banner_subtitle", e.target.value)}
            placeholder="ex.: A saga completa em 11 episódios"
          />
        </div>

        <div className="adm__campo">
          <label>Link de destino (opcional)</label>
          <input
            className="adm__input"
            value={d.banner_action_url}
            onChange={(e) => campo("banner_action_url", e.target.value)}
            placeholder="/serie/moises"
            style={{ fontFamily: '"Roboto Mono", monospace', fontSize: 12.5 }}
          />
          {!d.banner_action_url.trim() && (
            <p style={{ fontSize: 11.5, color: "var(--text3)", margin: "8px 0 0" }}>
              Sem link, tocar no banner não leva a criança a lugar nenhum.
            </p>
          )}
        </div>

        <div className="adm__modalAcoes">
          <button className="adm__btnGhost" onClick={onFechar}>
            Cancelar
          </button>
          <button className="adm__btn" disabled={!valido} onClick={() => onSalvar(d)}>
            {d.banner_id ? "Salvar banner" : "Criar banner"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Excluir é imediato e visível pra todo mundo — a confirmação diz isso, não "tem certeza?". */
function ConfirmarExclusao({ banner, onCancelar, onOk }) {
  const nome = banner.banner_title || "sem título";

  return (
    <div className="adm__modalFundo" onClick={onCancelar} style={{ zIndex: 200 }}>
      <div
        className="adm__modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(420px, 100%)" }}
      >
        <h3 style={{ color: "var(--cancel)" }}>Excluir banner</h3>
        <p className="adm__modalHint">
          O banner <strong>“{nome}”</strong> sai do carrossel do topo da home
          imediatamente — na próxima vez que a criança abrir o app, ele não estará mais lá.
        </p>

        <div className="adm__modalAcoes">
          <button className="adm__btnGhost" onClick={onCancelar}>
            Cancelar
          </button>
          <button className="adm__btn adm__btnDanger" onClick={onOk}>
            Excluir banner
          </button>
        </div>
      </div>
    </div>
  );
}
